import React from "react";
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Paper,
  useTheme,
  alpha
} from "@mui/material";
import { motion } from "framer-motion";
import PublicIcon from '@mui/icons-material/Public';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DiamondIcon from '@mui/icons-material/Diamond';

const features = [
  {
    icon: <PublicIcon sx={{ fontSize: 40 }} />,
    title: "Global Shipping",
    description: "Fully insured delivery to over 150 countries with discreet packaging and real-time tracking.",
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: "Secure Escrow Payments",
    description: "Institutional-grade escrow service protecting both buyers and sellers until transaction completion.",
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 40 }} />,
    title: "Certified Authenticity",
    description: "Every coin verified by PCGS, NGC, or our in-house numismatic experts with lifetime guarantee.",
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
    title: "Expert Valuation",
    description: "Comprehensive market analysis and fair market valuations from leading numismatic authorities.",
  },
  {
    icon: <DiamondIcon sx={{ fontSize: 40 }} />,
    title: "Discreet Transactions",
    description: "Complete privacy and confidentiality for high-net-worth individuals and institutional clients.",
  },
  {
    icon: <LocalShippingIcon sx={{ fontSize: 40 }} />,
    title: "White-Glove Service",
    description: "Personalized concierge service for premium collections and portfolio management.",
  }
];

const WhyChooseUs = () => {
  const theme = useTheme();

  return (
    <Box sx={{ 
      py: { xs: 8, md: 12 },
      background: '#FFFFFF',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Decorative Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.02,
          backgroundImage: 'radial-gradient(circle at 2px 2px, #C6A75E 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />

      <Container maxWidth="xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              textAlign: 'center',
              color: 'primary.main',
              mb: 2,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
            }}
          >
            Why Choose Us
          </Typography>
          
          <Typography variant="h2" sx={{ textAlign: 'center', mb: 3, color: '#2D2D2D' }}>
            The Diamond
            <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
              Standard
            </Box>
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              mb: 8,
              color: '#616161',
              fontSize: '1.2rem',
            }}
          >
            We've established the highest standards in the numismatic industry, 
            ensuring every transaction meets our exacting criteria for quality, 
            security, and service.
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#9E9E9E', 0.15),
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                      '& .feature-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                        color: 'primary.main',
                      }
                    },
                  }}
                >
                  <Box 
                    className="feature-icon"
                    sx={{ 
                      color: 'primary.main',
                      mb: 3,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                    {feature.title}
                  </Typography>
                  
                  <Typography variant="body2" color="#616161" sx={{ lineHeight: 1.8 }}>
                    {feature.description}
                  </Typography>

                  {/* Decorative Line */}
                  <Box
                    sx={{
                      width: 40,
                      height: 2,
                      backgroundColor: 'primary.main',
                      mt: 3,
                      opacity: 0.3,
                    }}
                  />
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Trust Badges */}
        <Box 
          sx={{ 
            mt: 8, 
            display: 'flex', 
            justifyContent: 'center',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          {['PCGS', 'NGC', 'ANACS', 'ICTA', 'PNG'].map((badge) => (
            <Paper
              key={badge}
              sx={{
                px: 4,
                py: 2,
                backgroundColor: 'transparent',
                border: '1px solid',
                borderColor: alpha('#9E9E9E', 0.2),
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: alpha('#C6A75E', 0.02),
                },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, letterSpacing: '0.1em', color: '#2D2D2D' }}>
                {badge}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default WhyChooseUs;